import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';

// Firebase configuration interface
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Get Firebase configuration from environment variables
const firebaseConfig: FirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY!,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.REACT_APP_FIREBASE_APP_ID!,
};

// Validate configuration
const validateFirebaseConfig = (config: FirebaseConfig): void => {
  const requiredFields: (keyof FirebaseConfig)[] = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ];

  const missingFields = requiredFields.filter(field => !config[field]);
  
  if (missingFields.length > 0) {
    throw new Error(
      `Missing Firebase configuration fields: ${missingFields.join(', ')}. ` +
      'Please check your environment variables.'
    );
  }
};

// Validate configuration before initializing
validateFirebaseConfig(firebaseConfig);

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Initialize Analytics only in production
export const analytics = 
  process.env.REACT_APP_ENABLE_ANALYTICS === 'true' && 
  process.env.NODE_ENV === 'production' 
    ? getAnalytics(app) 
    : null;

// Connect to emulators in development
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
  try {
    // Connect Auth emulator
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    
    // Connect Firestore emulator
    connectFirestoreEmulator(db, 'localhost', 8080);
    
    // Connect Storage emulator
    connectStorageEmulator(storage, 'localhost', 9199);
    
    console.log('🔧 Connected to Firebase emulators');
  } catch (error) {
    console.warn('⚠️ Could not connect to Firebase emulators (may already be connected):', error);
  }
}

// Export the Firebase app instance
export default app;

// Firebase configuration object for external use
export { firebaseConfig };

// Helper function to check if Firebase is properly configured
export const isFirebaseConfigured = (): boolean => {
  try {
    validateFirebaseConfig(firebaseConfig);
    return true;
  } catch {
    return false;
  }
};

// Environment information
export const firebaseEnvironment = {
  projectId: firebaseConfig.projectId,
  environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  emulators: process.env.REACT_APP_USE_EMULATORS === 'true',
  analytics: !!analytics,
};

// Log Firebase initialization
console.log('🔥 Firebase initialized:', {
  projectId: firebaseConfig.projectId,
  environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  emulators: process.env.REACT_APP_USE_EMULATORS === 'true'
});